import { cx } from '@emotion/css';
import React, { EventHandler, useRef, useState } from 'react';
import { SizeVariants } from '../../../theme/themes';
import { makeCSS } from '../../../utils/makeCSS';
import { iconFiles } from '../Icon/files';
import { InputFieldTempate, InputFieldTempateProps } from './InputFieldTempate';
import { Message } from './Message';
import { uuidv4 } from '../../../../../state/src/utils/uuid';
import { TextareaAutosize } from '@material-ui/core';
import { VBox } from '../../templates/layout/Flex';

/**
 * InputFields provides users the means to enter non-standardized responses.
 * It's used to create interactive controls, in order to collect and retrieve user data.
 */
export interface InputFieldProps extends Pick<
  InputFieldTempateProps,
  'iconRight'
> {
  /**Placeholder of the search input */
  placeholder?: string;
  /**When new text is filled or when the enter is clicked */
  onSubmit?(value: string): void;
  /**Icon on the left side of the inputfield */
  iconLeft?: keyof typeof iconFiles;
  /**Funtionality of the left icon */
  onLeftIconClick?: EventHandler<React.MouseEvent>;
  /**Funtionality of the right icon */
  onRightIconClick?: EventHandler<React.MouseEvent>;
  autoIconSize?: boolean;
  /**Different sizes*/
  size?: SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  width?: number | string;
  /** use full available width  */
  fullwidth?: boolean;
  /**Disable the search input*/
  disabled?: boolean;
  /**When there is something wrong with the inputfield */
  criticalState?: boolean;
  warningState?: boolean;
  /**Max number of rows for the field */
  multiline?: number; //Only for InputField/Message
  /** Allow textarea to grow and shrink automatically. The `multiline` prop value is used as a maximum value. */
  autoResize?: boolean;
  /**Show or hide characher count*/
  showCount?: boolean;
  /**How many characters to be allowed */
  characterCount?: number;
  /**Count how many character are left or how many have been already added */
  countVariant?: 'charLeft' | 'charIn' | 'custom';
  /**Customizable number for counting area, users can add their on functionality*/
  customNumber?: number;
  /** */
  value?: string;
  onChange?(value: string): void;
  /*________ */
  /**Message underneath the inputfield, used for comments or notifications*/
  messageLabel?: string | string[];
  /**Use the message*/
  showMessage?: boolean;
  /***/
  messageIcon?: keyof typeof iconFiles;
  /**Different colors of the icons and the text */ //Only for InputField/Message
  messageColor?: 'default' | 'positive' | 'warning' | 'critical';
  autoFocus?: boolean;
  autoComplete?: string;
  /** set to false to try to prevent browser autofill */
  autoFill?: boolean;
  /**the type of the input we want to use */
  type?: string;
  /** name used for forms */
  name?: string;
  /** id used for forms and other selecting*/
  id?: string;
  /** Extra component that will be added to the start */
  prefixElement?: React.ReactNode;
  /** Extra component that will be added to the end */
  suffixElement?: React.ReactNode;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'start',
      gap: theme.spacing[2],
      // paddingTop: theme.spacing[3],
      // paddingBottom: theme.spacing[3],
      //remove autofill style
      'input:is(:-webkit-autofill, :autofill)': {
        WebkitTextFillColor: theme.color.text['01'],
        transition: 'background-color 5000s ease-in-out 0s'
      }
    },
    textarea: {
      width: '100%',
      alignContent: 'bottom',
      resize: 'none',
      color: theme.color.text['01'],
      fontFamily: 'inherit',
      fontSize: 'inherit',
      background: 'inherit',
      paddingTop: theme.spacing['2'],
      paddingBottom: theme.spacing['2'],
      marginTop: 0,
      outline: 'none',
      border: 'inherit',
      '&::placeholder': {
        color: theme.color.text['04']
      }
    },
    text: {
      width: '100%',
      alignContent: 'bottom',
      resize: 'none',
      color: theme.color.text['01'],
      fontFamily: 'inherit',
      fontSize: 'inherit',
      background: 'inherit',
      padding: 0,
      paddingTop: 1,
      marginTop: 0,
      outline: 'none',
      border: 'inherit',
      '&::placeholder': {
        color: theme.color.text['04']
      }
    },
    word_count: {
      paddingLeft: theme.spacing['0,5'],
      paddingRight: theme.spacing['0,5'],
      borderRadius: theme.spacing[1],
      background: theme.color.background.white,
      color: theme.color.text.light['01'],
      fontWeight: theme.fontWeights.medium,
      userSelect: 'none'
    },
    suffix: {
      paddingLeft: theme.spacing['0,5'],
      paddingRight: theme.spacing['0,5']
    }
  };
});

const InputField = (
  props: InputFieldProps &
    Omit<
      Partial<
        | React.DetailedHTMLProps<
          React.InputHTMLAttributes<HTMLInputElement>,
          HTMLInputElement
        >
        | React.DetailedHTMLProps<
          React.TextareaHTMLAttributes<HTMLTextAreaElement>,
          HTMLTextAreaElement
        >
      >,
      'onChange'
    >
) => {
  const {
    placeholder = 'Start typing...',
    onSubmit,
    iconLeft,
    onLeftIconClick,
    iconRight,
    onRightIconClick,
    size = SizeVariants.md,
    fullwidth = false,
    disabled = false,
    criticalState = false,
    warningState = false,
    multiline = 2,
    autoResize,
    showCount = false,
    characterCount = 99,
    countVariant = 'charIn',
    customNumber,
    width,
    onChange,
    //
    messageLabel,
    showMessage = false,
    messageIcon = undefined,
    messageColor = 'default',
    value = '',
    autoIconSize,
    autoFill = false,
    type,
    name,
    id,
    suffixElement,
    prefixElement,
    // input
    ...inputProps
  } = props;

  const classes = useCSS();
  const inputRef = useRef(null);

  const [input, setInput] = useState(props.value || '');

  const characterCountValue =
    countVariant === 'charIn'
      ? input.replace(/\s/g, '').length
      : characterCount - input.replace(/\s/g, '').length;

  const inputHandler = (event) => {
    setInput(event.target.value);
    onChange?.(event.target.value);
  };

  //when user clicks on the inputfield the textarea inside of it gets focused
  const startWriting = () => {
    inputRef.current.focus();
  };

  React.useLayoutEffect(() => {
    if (input !== value) {
      setInput(value);
    }
  }, [value]);

  //when the enter button is pushed (inside the text field)
  const handleKeyDown = (event) => {
    if (input != '') {
      if (event.keyCode === 13) {
        if (multilineOn && event.shiftKey) {
          return;
        }
        event.preventDefault();
        onSubmit?.(input);
      }
    }
  };
  let multilineOn = false;
  if (multiline > 1) {
    multilineOn = true;
  }

  return (
    <div>
      <InputFieldTempate
        width={fullwidth ? 'auto' : width || 80}
        label={''}
        criticalState={criticalState}
        warningState={warningState}
        size={size}
        iconLeft={iconLeft}
        onLeftIconClick={onLeftIconClick}
        iconRight={iconRight}
        onRightIconClick={onRightIconClick}
        disabled={disabled}
        useMultiline={multilineOn}
        showCount={
          showCount ? (
            <div
              className={cx(classes.word_count, {
                disabled: disabled
              })}
            >
              {/**whether the number displayed will be the characters left of the character that are already in the text field*/}
              {countVariant === 'custom' ? (
                <div>{customNumber}</div>
              ) : (
                <div>{characterCountValue}</div>
              )}
            </div>
          ) : (
            ''
          )
        }
      >
        <div onClick={startWriting} className={`${classes.root}`}>
          {prefixElement}
          {multilineOn ? (
            autoResize ? (
              <TextareaAutosize
                {...(inputProps as React.DetailedHTMLProps<
                  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
                  HTMLTextAreaElement
                >)}
                disabled={disabled}
                ref={inputRef}
                onKeyDown={handleKeyDown}
                className={`${classes.textarea}`}
                placeholder={placeholder}
                onChange={inputHandler}
                value={input}
                id={id}
                name={name}
                autoComplete={!autoFill ? uuidv4() : undefined}
                minRows={multiline}
                maxRows={multiline ? multiline * 10 : undefined}
              />
            ) : (
              <textarea
                {...(inputProps as React.DetailedHTMLProps<
                  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
                  HTMLTextAreaElement
                >)}
                disabled={disabled}
                ref={inputRef}
                onKeyDown={handleKeyDown}
                className={`${classes.textarea}`}
                placeholder={placeholder}
                rows={multiline}
                onChange={inputHandler}
                value={input}
                id={id}
                name={name}
                autoComplete={!autoFill ? uuidv4() : undefined}
              ></textarea>
            )
          ) : (
            <input
              {...(inputProps as React.DetailedHTMLProps<
                React.InputHTMLAttributes<HTMLInputElement>,
                HTMLInputElement
              >)}
              disabled={disabled}
              ref={inputRef}
              onKeyDown={handleKeyDown}
              className={`${classes.text}`}
              placeholder={placeholder}
              onChange={inputHandler}
              value={input}
              type={type}
              id={id || (!autoFill ? uuidv4() : inputProps.autoComplete)}
              name={name || (!autoFill ? uuidv4() : inputProps.autoComplete)}
              autoCorrect={!autoFill ? 'off' : ''}
              spellCheck={!autoFill}
              autoComplete={!autoFill ? uuidv4() : inputProps.autoComplete}
            />
          )}
          {suffixElement}
        </div>
      </InputFieldTempate>
      {showMessage && <div style={{ marginTop: 4 }} />}
      {showMessage &&
        messageLabel &&
        (Array.isArray(messageLabel) ? (
          <VBox>
            {messageLabel.map(
              (msg) =>
                msg && (
                  <Message
                    key={msg}
                    label={msg}
                    messageColor={criticalState ? 'critical' : messageColor}
                    icon={messageIcon}
                    fullWidth
                  ></Message>
                )
            )}
          </VBox>
        ) : (
          <Message
            label={messageLabel}
            messageColor={criticalState ? 'critical' : messageColor}
            icon={messageIcon}
            fullWidth
          ></Message>
        ))}
    </div>
  );
};

//const Default = withCubicleFallback(InputField);
export default InputField;
export { InputField };
